import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { DatepickerRange } from '@app/shared/models/datepicker-range.model'

// Extend dayjs with necessary plugins
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrAfter)

export interface DateRange {
  from: Dayjs
  to: Dayjs
  sameTimeEachDay: boolean
}

@Component({
  selector: 'lin-date-range',
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DateRangeComponent implements OnInit {
  form: FormGroup

  @Input() dateFrom: Dayjs
  @Input() dateTo: Dayjs
  @Input() defaultRange: 'lastMonth' | 'lastWeek' | 'today'
  @Input() isDisabled: boolean
  @Input() isLoading: boolean
  @Input() isReport: boolean
  @Input() label: string
  @Input() showEachDayCheckbox = false
  @Output() dateRangeChange = new EventEmitter<DateRange>()

  private storedTimezone: string | undefined

  constructor(private formBuilder: FormBuilder) {
    this.form = this.formBuilder.group({
      selected: {},
    })
  }

  ngOnInit(): void {
    this.initializeDates()
  }

  private initializeDates(): void {
    this.form.setValue({
      selected: {
        startDate: this.dateFrom || this.defaultDateFrom,
        endDate: this.dateTo || this.defaultDateTo,
      },
    })

    this.form.markAsDirty()
  }

  private get startOfYesterday(): Dayjs {
    return dayjs().tz().subtract(1, 'day').startOf('day')
  }

  private get endOfYesterday(): Dayjs {
    return dayjs().tz().subtract(1, 'day').endOf('day')
  }

  private get startOfToday(): Dayjs {
    return dayjs().tz().startOf('day')
  }

  private get endOfToday(): Dayjs {
    return dayjs().tz().endOf('day')
  }

  private get startOfPastMonth(): Dayjs {
    return dayjs().tz().subtract(1, 'month').startOf('month').startOf('day')
  }

  private get endOfPastMonth(): Dayjs {
    return dayjs().tz().subtract(1, 'month').endOf('month').endOf('day')
  }

  private get startOfLastWeek(): Dayjs {
    return dayjs().tz().subtract(6, 'days').startOf('day')
  }

  private get endOfLastWeek(): Dayjs {
    return dayjs().tz().endOf('day')
  }

  private get defaultDateFrom(): Dayjs {
    if (this.isReport) {
      return this.startOfYesterday
    }

    switch (this.defaultRange) {
      case 'today':
        return this.startOfToday
      case 'lastWeek':
        return this.startOfLastWeek
      default:
        return this.startOfPastMonth
    }
  }

  private get defaultDateTo(): Dayjs {
    if (this.isReport) {
      return this.endOfYesterday
    }

    switch (this.defaultRange) {
      case 'today':
        return this.endOfToday
      case 'lastWeek':
        return this.endOfLastWeek
      default:
        return this.endOfPastMonth
    }
  }

  private getTimezone(date: Dayjs): string | undefined {
    // Accessing Dayjs internal $x.$timezone which is not part of the public type definition
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
    const timezone = (date as unknown as { $x?: { $timezone?: string } })?.$x?.$timezone

    this.storedTimezone = timezone || this.storedTimezone
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return timezone
  }

  getValue(): DateRange {
    // form.value is typed as `any` in FormGroup — cast is intentional
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const {
      selected: { startDate: from, endDate: to },
    } = this.form.value as { selected: DatepickerRange<Dayjs> }

    const timezone = this.getTimezone(from) || this.storedTimezone

    // Directly use the provided startDate and endDate
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument
    const start = dayjs(from).tz(timezone, true)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument
    const end = dayjs(to).tz(timezone, true)

    const dateRange = {
      from: start,
      to: end,
      sameTimeEachDay: localStorage.getItem('linxio_time_range_each_day') === 'true',
    }

    return dateRange
  }

  updateSearch(): void {
    const dateRange = this.getValue()
    sessionStorage.setItem('skipApiCall', JSON.stringify(false))
    this.dateRangeChange.emit(dateRange)
  }

  onDateRangeChange(dateValues: DatepickerRange<Dayjs>): void {
    const timezone = this.getTimezone(dateValues.startDate) || this.storedTimezone

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument
    const from = dayjs(dateValues.startDate).tz(timezone, true)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument
    const to = dayjs(dateValues.endDate).tz(timezone, true)

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const currentFrom = this.form.get('selected.startDate')?.value
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const currentTo = this.form.get('selected.endDate')?.value

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    if (!currentFrom?.isSame(from) || !currentTo?.isSame(to)) {
      this.form.patchValue({
        selected: {
          startDate: from,
          endDate: to,
        },
      })

      this.form.markAsDirty()
    }
  }
}
